/* eslint-disable */
import Long from "long";
import _m0 from "protobufjs/minimal";
import { Namespace } from "./namespace";
import { BenchmarkResult } from "./benchmark";
import { Ruid } from "../../common/ruid";
import { BoolValue } from "../../google/protobuf/wrappers";

export const protobufPackage = "snapchat.cdp.cof";

/** Indicates what was the state of the app when the config sync was triggered */
export enum ConfigTargetingTriggerEventType {
  /** UNKNOWN_EVENT_TYPE - Unknown */
  UNKNOWN_EVENT_TYPE = 0,
  /** COLD_START - Deprecated because of simplification to FG/BG */
  COLD_START = 1,
  /** WARM_START - Deprecated because of simplification to FG/BG */
  WARM_START = 2,
  /** FOREGROUND_TRIGGER - Triggered when application is in the foreground */
  FOREGROUND_TRIGGER = 3,
  /** BACKGROUND_TRIGGER - Triggered when application is backgrounded */
  BACKGROUND_TRIGGER = 4,
  UNRECOGNIZED = -1,
}

/**
 * Indicates what was the state of the app when the config sync happens
 * The enum is also used as a dynamic client property to populate feature signals
 */
export enum ConfigTargetingAppState {
  /** UNKNOWN_APP_STATE - Unknown */
  UNKNOWN_APP_STATE = 0,
  /** FOREGROUND - Foreground */
  FOREGROUND = 1,
  /** BACKGROUND - Background */
  BACKGROUND = 2,
  UNRECOGNIZED = -1,
}

/**
 * We expect this enum to be updated whenever there is a new sync instrumentation: This will help us isolate new changes on
 * server side to avoid post mortems like https://snapchat.quip.com/xGnVAcsBrOeC
 */
export enum ConfigTargetingInstrumentation {
  /** DEFAULT_INSTRUMENTATION - Default */
  DEFAULT_INSTRUMENTATION = 0,
  /** USER_AUTHENTICATION - User logged in */
  USER_AUTHENTICATION = 1,
  UNRECOGNIZED = -1,
}

/**
 * The request contains targeting signals that are not already part of the User Agent.
 * Note the user agent is passed via the standard HTTP header hence not part of the request proto.
 */
export interface ConfigTargetingRequest {
  /**
   * The previously received etag, an empty list of ConfigResult will be returned if this matches
   * the newly computed etag.
   */
  configResultsEtag: string;
  /**
   * Caller can provide at most one field from "namespaces" and "config_names"
   * If not empty only return the matched configs in these namespaces.
   * If empty return all matched configs.
   */
  namespaces: Namespace[];
  /**
   * Deprecated
   *
   * @deprecated
   */
  ghostId: string;
  /**
   * Derive country code from IP rather than request payload.
   * IP is passed via this header 'x-snap-client-ip'.
   * ISO ALPHA-2 Country Code (lowercase)
   *
   * @deprecated
   */
  countryCode: string;
  /** The width in pixel of client screen. */
  screenWidth: number;
  /** The height in pixel of client screen. */
  screenHeight: number;
  /** Current device connectivity */
  connectivity: Connectivity | undefined;
  /** The maximum playable video width (assumes portrait orientation) */
  maxVideoWidthPx: number;
  /** The maximum playable video height (assumes portrait orientation) */
  maxVideoHeightPx: number;
  /** Client reports results from benchmark executions. */
  benchmarkResults: BenchmarkResult[];
  /** If set the server will only return the config results that are new or have changed. */
  deltaSync: boolean;
  /**
   * User Agent used for server to server request, which takes precedence over the User-Agent header
   * UA is passed via this header 'x-snap-client-user-agent'.
   *
   * @deprecated
   */
  userAgent: string;
  /** Type of event that triggers client to sync server */
  triggerEventType: ConfigTargetingTriggerEventType;
  /** App state when config targeting sync happens */
  appState: ConfigTargetingAppState;
  /**
   * Locale of the client.
   *
   * @deprecated
   */
  locale: string;
  /** Need to set this field for AB service if user id is not available (before user login). */
  deviceId: string;
  /**
   * Deprecated! This field won't be used! For server-initiated requests, set userId in "x-snap-cof-principal" header
   *
   * @deprecated
   */
  userId: string;
  /**
   * client ip is used for server to client ip to cof server.
   *
   * @deprecated
   */
  clientIp: string;
  /** If true, this is a request before user login. */
  isUnAuthorized: boolean;
  /** App locale in client. This may be different from device locale, which will be passed from header "Accept-Language". */
  appLocale: string;
  /** Location of config targeting sync invocation. */
  instrumentation: ConfigTargetingInstrumentation;
  /**
   * Timestamp in seconds from unix epoc that is the last time that this client was able to sync with the service.
   * This is used for us to optimize delta syncing infrastructure.
   */
  lastSuccessfulSync: number;
  /** This sync triggered by user logout. */
  isLogout: boolean;
  /** The source of app install (Android Only) */
  packageInstaller: string;
  /** Session ID that blizzard has when a COF sync is triggered */
  syncTriggerBlizzardSessionId: string;
  /** Session ID that blizzard has when a COF sync is executed */
  syncExecutionBlizzardSessionId: string;
  /** The delay between when the app is started and cof sync is triggered */
  cofSyncTriggerDelayFromStartupMs: number;
  /** The delay between when the app is started and cof sync starts to execute */
  cofSyncExecutionDelayFromStartupMs: number;
  /** Timestamp in seconds of the time the sync was triggered */
  syncTriggerTime: number;
  /** Allows client to signal what kind of media encoders/decoders are available */
  decoderEncoderAvailability: DecoderEncoderAvailablity | undefined;
  /** SnapKit app id from SnapKit Dev Portal */
  snapkitAppId: string;
  /** Lens core version which will be passed by all clients */
  lenscoreVersion: number;
  /**
   * Can represent any ID such as user_id, device_id, web_client_id; When present, its value override the corresponding
   * fields in the request
   */
  ruid: Ruid | undefined;
  /**
   * Caller can provide at most one field from "namespaces" and "config_names"
   * If not empty only return the matched configs
   */
  configNames: string[];
  /**
   * If true response will include TEST_USER allowlisted treatments
   * This is necessary for pre-login syncs where we do not yet know the user that will be logged in
   * Only works on internal builds AND if user is not logged-in.
   * See https://jira.sc-corp.net/browse/DAPSUPPORT-3363 for more context.
   */
  includeTestUserTreatments: boolean;
  /** whether to log exposure logging, default is to log exposure */
  disableExposureLogging: boolean;
  /**
   * Override value for the LENS_CLUSTER_ORIG_4 targeting property so that browser clients can
   * implement their own device benchmarking for lenses.
   */
  lensClusterOrig4: number;
  /**
   * ClientID is similar to device ID but it rotates after 30 days.
   * Used for allocation logs for device id AB Studies triggered in the server side
   */
  clientId: string;
}

/** Connectivity */
export interface Connectivity {
  /** Current network connection type */
  networkType: Connectivity_NetworkType;
  /**
   * A network is classified as metered when the user is sensitive to heavy data usage
   * on that connection due to monetary costs, data limitations or battery/performance issues.
   */
  isMetered: boolean | undefined;
  /** Whether or not client is roaming */
  isRoaming: boolean | undefined;
  /** String representing what carrier is providing the device with internet. */
  carrier: string;
}

/** NetworkType */
export enum Connectivity_NetworkType {
  /** UNKNOWN_NETWORK_TYPE - Unknown */
  UNKNOWN_NETWORK_TYPE = 0,
  /** CELLULAR - Cellular */
  CELLULAR = 1,
  /** WIFI - Wifi */
  WIFI = 2,
  UNRECOGNIZED = -1,
}

export interface DecoderEncoderAvailablity {
  /** Signals that snapchat's custom VP9 software decoder library is available on the client */
  isSnapVp9DecoderAvailable: boolean;
  /** Signals that snapchat's custom AV1 software decoder library is available on the client */
  isSnapAv1DecoderAvailable: boolean;
}

function createBaseConfigTargetingRequest(): ConfigTargetingRequest {
  return {
    configResultsEtag: "",
    namespaces: [],
    ghostId: "",
    countryCode: "",
    screenWidth: 0,
    screenHeight: 0,
    connectivity: undefined,
    maxVideoWidthPx: 0,
    maxVideoHeightPx: 0,
    benchmarkResults: [],
    deltaSync: false,
    userAgent: "",
    triggerEventType: 0,
    appState: 0,
    locale: "",
    deviceId: "",
    userId: "",
    clientIp: "",
    isUnAuthorized: false,
    appLocale: "",
    instrumentation: 0,
    lastSuccessfulSync: 0,
    isLogout: false,
    packageInstaller: "",
    syncTriggerBlizzardSessionId: "",
    syncExecutionBlizzardSessionId: "",
    cofSyncTriggerDelayFromStartupMs: 0,
    cofSyncExecutionDelayFromStartupMs: 0,
    syncTriggerTime: 0,
    decoderEncoderAvailability: undefined,
    snapkitAppId: "",
    lenscoreVersion: 0,
    ruid: undefined,
    configNames: [],
    includeTestUserTreatments: false,
    disableExposureLogging: false,
    lensClusterOrig4: 0,
    clientId: "",
  };
}

export const ConfigTargetingRequest = {
  encode(message: ConfigTargetingRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.configResultsEtag !== "") {
      writer.uint32(10).string(message.configResultsEtag);
    }
    writer.uint32(18).fork();
    for (const v of message.namespaces) {
      writer.int32(v);
    }
    writer.ldelim();
    if (message.ghostId !== "") {
      writer.uint32(26).string(message.ghostId);
    }
    if (message.countryCode !== "") {
      writer.uint32(34).string(message.countryCode);
    }
    if (message.screenWidth !== 0) {
      writer.uint32(40).int32(message.screenWidth);
    }
    if (message.screenHeight !== 0) {
      writer.uint32(48).int32(message.screenHeight);
    }
    if (message.connectivity !== undefined) {
      Connectivity.encode(message.connectivity, writer.uint32(58).fork()).ldelim();
    }
    if (message.maxVideoWidthPx !== 0) {
      writer.uint32(64).int32(message.maxVideoWidthPx);
    }
    if (message.maxVideoHeightPx !== 0) {
      writer.uint32(72).int32(message.maxVideoHeightPx);
    }
    for (const v of message.benchmarkResults) {
      BenchmarkResult.encode(v!, writer.uint32(82).fork()).ldelim();
    }
    if (message.deltaSync === true) {
      writer.uint32(88).bool(message.deltaSync);
    }
    if (message.userAgent !== "") {
      writer.uint32(98).string(message.userAgent);
    }
    if (message.triggerEventType !== 0) {
      writer.uint32(104).int32(message.triggerEventType);
    }
    if (message.appState !== 0) {
      writer.uint32(112).int32(message.appState);
    }
    if (message.locale !== "") {
      writer.uint32(122).string(message.locale);
    }
    if (message.deviceId !== "") {
      writer.uint32(130).string(message.deviceId);
    }
    if (message.userId !== "") {
      writer.uint32(138).string(message.userId);
    }
    if (message.clientIp !== "") {
      writer.uint32(146).string(message.clientIp);
    }
    if (message.isUnAuthorized === true) {
      writer.uint32(152).bool(message.isUnAuthorized);
    }
    if (message.appLocale !== "") {
      writer.uint32(162).string(message.appLocale);
    }
    if (message.instrumentation !== 0) {
      writer.uint32(168).int32(message.instrumentation);
    }
    if (message.lastSuccessfulSync !== 0) {
      writer.uint32(176).int64(message.lastSuccessfulSync);
    }
    if (message.isLogout === true) {
      writer.uint32(184).bool(message.isLogout);
    }
    if (message.packageInstaller !== "") {
      writer.uint32(194).string(message.packageInstaller);
    }
    if (message.syncTriggerBlizzardSessionId !== "") {
      writer.uint32(202).string(message.syncTriggerBlizzardSessionId);
    }
    if (message.syncExecutionBlizzardSessionId !== "") {
      writer.uint32(210).string(message.syncExecutionBlizzardSessionId);
    }
    if (message.cofSyncTriggerDelayFromStartupMs !== 0) {
      writer.uint32(216).int32(message.cofSyncTriggerDelayFromStartupMs);
    }
    if (message.cofSyncExecutionDelayFromStartupMs !== 0) {
      writer.uint32(224).int32(message.cofSyncExecutionDelayFromStartupMs);
    }
    if (message.syncTriggerTime !== 0) {
      writer.uint32(232).int64(message.syncTriggerTime);
    }
    if (message.decoderEncoderAvailability !== undefined) {
      DecoderEncoderAvailablity.encode(message.decoderEncoderAvailability, writer.uint32(242).fork()).ldelim();
    }
    if (message.snapkitAppId !== "") {
      writer.uint32(250).string(message.snapkitAppId);
    }
    if (message.lenscoreVersion !== 0) {
      writer.uint32(256).int32(message.lenscoreVersion);
    }
    if (message.ruid !== undefined) {
      Ruid.encode(message.ruid, writer.uint32(266).fork()).ldelim();
    }
    for (const v of message.configNames) {
      writer.uint32(274).string(v!);
    }
    if (message.includeTestUserTreatments === true) {
      writer.uint32(288).bool(message.includeTestUserTreatments);
    }
    if (message.disableExposureLogging === true) {
      writer.uint32(296).bool(message.disableExposureLogging);
    }
    if (message.lensClusterOrig4 !== 0) {
      writer.uint32(304).int32(message.lensClusterOrig4);
    }
    if (message.clientId !== "") {
      writer.uint32(314).string(message.clientId);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ConfigTargetingRequest {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseConfigTargetingRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.configResultsEtag = reader.string();
          break;
        case 2:
          if ((tag & 7) === 2) {
            const end2 = reader.uint32() + reader.pos;
            while (reader.pos < end2) {
              message.namespaces.push(reader.int32() as any);
            }
          } else {
            message.namespaces.push(reader.int32() as any);
          }
          break;
        case 3:
          message.ghostId = reader.string();
          break;
        case 4:
          message.countryCode = reader.string();
          break;
        case 5:
          message.screenWidth = reader.int32();
          break;
        case 6:
          message.screenHeight = reader.int32();
          break;
        case 7:
          message.connectivity = Connectivity.decode(reader, reader.uint32());
          break;
        case 8:
          message.maxVideoWidthPx = reader.int32();
          break;
        case 9:
          message.maxVideoHeightPx = reader.int32();
          break;
        case 10:
          message.benchmarkResults.push(BenchmarkResult.decode(reader, reader.uint32()));
          break;
        case 11:
          message.deltaSync = reader.bool();
          break;
        case 12:
          message.userAgent = reader.string();
          break;
        case 13:
          message.triggerEventType = reader.int32() as any;
          break;
        case 14:
          message.appState = reader.int32() as any;
          break;
        case 15:
          message.locale = reader.string();
          break;
        case 16:
          message.deviceId = reader.string();
          break;
        case 17:
          message.userId = reader.string();
          break;
        case 18:
          message.clientIp = reader.string();
          break;
        case 19:
          message.isUnAuthorized = reader.bool();
          break;
        case 20:
          message.appLocale = reader.string();
          break;
        case 21:
          message.instrumentation = reader.int32() as any;
          break;
        case 22:
          message.lastSuccessfulSync = longToNumber(reader.int64() as Long);
          break;
        case 23:
          message.isLogout = reader.bool();
          break;
        case 24:
          message.packageInstaller = reader.string();
          break;
        case 25:
          message.syncTriggerBlizzardSessionId = reader.string();
          break;
        case 26:
          message.syncExecutionBlizzardSessionId = reader.string();
          break;
        case 27:
          message.cofSyncTriggerDelayFromStartupMs = reader.int32();
          break;
        case 28:
          message.cofSyncExecutionDelayFromStartupMs = reader.int32();
          break;
        case 29:
          message.syncTriggerTime = longToNumber(reader.int64() as Long);
          break;
        case 30:
          message.decoderEncoderAvailability = DecoderEncoderAvailablity.decode(reader, reader.uint32());
          break;
        case 31:
          message.snapkitAppId = reader.string();
          break;
        case 32:
          message.lenscoreVersion = reader.int32();
          break;
        case 33:
          message.ruid = Ruid.decode(reader, reader.uint32());
          break;
        case 34:
          message.configNames.push(reader.string());
          break;
        case 36:
          message.includeTestUserTreatments = reader.bool();
          break;
        case 37:
          message.disableExposureLogging = reader.bool();
          break;
        case 38:
          message.lensClusterOrig4 = reader.int32();
          break;
        case 39:
          message.clientId = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromPartial<I extends Exact<DeepPartial<ConfigTargetingRequest>, I>>(object: I): ConfigTargetingRequest {
    const message = createBaseConfigTargetingRequest();
    message.configResultsEtag = object.configResultsEtag ?? "";
    message.namespaces = object.namespaces?.map((e) => e) || [];
    message.ghostId = object.ghostId ?? "";
    message.countryCode = object.countryCode ?? "";
    message.screenWidth = object.screenWidth ?? 0;
    message.screenHeight = object.screenHeight ?? 0;
    message.connectivity =
      object.connectivity !== undefined && object.connectivity !== null
        ? Connectivity.fromPartial(object.connectivity)
        : undefined;
    message.maxVideoWidthPx = object.maxVideoWidthPx ?? 0;
    message.maxVideoHeightPx = object.maxVideoHeightPx ?? 0;
    message.benchmarkResults = object.benchmarkResults?.map((e) => BenchmarkResult.fromPartial(e)) || [];
    message.deltaSync = object.deltaSync ?? false;
    message.userAgent = object.userAgent ?? "";
    message.triggerEventType = object.triggerEventType ?? 0;
    message.appState = object.appState ?? 0;
    message.locale = object.locale ?? "";
    message.deviceId = object.deviceId ?? "";
    message.userId = object.userId ?? "";
    message.clientIp = object.clientIp ?? "";
    message.isUnAuthorized = object.isUnAuthorized ?? false;
    message.appLocale = object.appLocale ?? "";
    message.instrumentation = object.instrumentation ?? 0;
    message.lastSuccessfulSync = object.lastSuccessfulSync ?? 0;
    message.isLogout = object.isLogout ?? false;
    message.packageInstaller = object.packageInstaller ?? "";
    message.syncTriggerBlizzardSessionId = object.syncTriggerBlizzardSessionId ?? "";
    message.syncExecutionBlizzardSessionId = object.syncExecutionBlizzardSessionId ?? "";
    message.cofSyncTriggerDelayFromStartupMs = object.cofSyncTriggerDelayFromStartupMs ?? 0;
    message.cofSyncExecutionDelayFromStartupMs = object.cofSyncExecutionDelayFromStartupMs ?? 0;
    message.syncTriggerTime = object.syncTriggerTime ?? 0;
    message.decoderEncoderAvailability =
      object.decoderEncoderAvailability !== undefined && object.decoderEncoderAvailability !== null
        ? DecoderEncoderAvailablity.fromPartial(object.decoderEncoderAvailability)
        : undefined;
    message.snapkitAppId = object.snapkitAppId ?? "";
    message.lenscoreVersion = object.lenscoreVersion ?? 0;
    message.ruid = object.ruid !== undefined && object.ruid !== null ? Ruid.fromPartial(object.ruid) : undefined;
    message.configNames = object.configNames?.map((e) => e) || [];
    message.includeTestUserTreatments = object.includeTestUserTreatments ?? false;
    message.disableExposureLogging = object.disableExposureLogging ?? false;
    message.lensClusterOrig4 = object.lensClusterOrig4 ?? 0;
    message.clientId = object.clientId ?? "";
    return message;
  },
};

function createBaseConnectivity(): Connectivity {
  return { networkType: 0, isMetered: undefined, isRoaming: undefined, carrier: "" };
}

export const Connectivity = {
  encode(message: Connectivity, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.networkType !== 0) {
      writer.uint32(8).int32(message.networkType);
    }
    if (message.isMetered !== undefined) {
      BoolValue.encode({ value: message.isMetered! }, writer.uint32(18).fork()).ldelim();
    }
    if (message.isRoaming !== undefined) {
      BoolValue.encode({ value: message.isRoaming! }, writer.uint32(26).fork()).ldelim();
    }
    if (message.carrier !== "") {
      writer.uint32(34).string(message.carrier);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Connectivity {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseConnectivity();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.networkType = reader.int32() as any;
          break;
        case 2:
          message.isMetered = BoolValue.decode(reader, reader.uint32()).value;
          break;
        case 3:
          message.isRoaming = BoolValue.decode(reader, reader.uint32()).value;
          break;
        case 4:
          message.carrier = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromPartial<I extends Exact<DeepPartial<Connectivity>, I>>(object: I): Connectivity {
    const message = createBaseConnectivity();
    message.networkType = object.networkType ?? 0;
    message.isMetered = object.isMetered ?? undefined;
    message.isRoaming = object.isRoaming ?? undefined;
    message.carrier = object.carrier ?? "";
    return message;
  },
};

function createBaseDecoderEncoderAvailablity(): DecoderEncoderAvailablity {
  return { isSnapVp9DecoderAvailable: false, isSnapAv1DecoderAvailable: false };
}

export const DecoderEncoderAvailablity = {
  encode(message: DecoderEncoderAvailablity, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.isSnapVp9DecoderAvailable === true) {
      writer.uint32(8).bool(message.isSnapVp9DecoderAvailable);
    }
    if (message.isSnapAv1DecoderAvailable === true) {
      writer.uint32(16).bool(message.isSnapAv1DecoderAvailable);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): DecoderEncoderAvailablity {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseDecoderEncoderAvailablity();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.isSnapVp9DecoderAvailable = reader.bool();
          break;
        case 2:
          message.isSnapAv1DecoderAvailable = reader.bool();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromPartial<I extends Exact<DeepPartial<DecoderEncoderAvailablity>, I>>(object: I): DecoderEncoderAvailablity {
    const message = createBaseDecoderEncoderAvailablity();
    message.isSnapVp9DecoderAvailable = object.isSnapVp9DecoderAvailable ?? false;
    message.isSnapAv1DecoderAvailable = object.isSnapAv1DecoderAvailable ?? false;
    return message;
  },
};

declare var self: any | undefined;
declare var window: any | undefined;
declare var global: any | undefined;
var globalThis: any = (() => {
  if (typeof globalThis !== "undefined") return globalThis;
  if (typeof self !== "undefined") return self;
  if (typeof window !== "undefined") return window;
  if (typeof global !== "undefined") return global;
  throw "Unable to locate global object";
})();

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin
  ? T
  : T extends Array<infer U>
  ? Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U>
  ? ReadonlyArray<DeepPartial<U>>
  : T extends {}
  ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin
  ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & Record<Exclude<keyof I, KeysOfUnion<P>>, never>;

function longToNumber(long: Long): number {
  if (long.gt(Number.MAX_SAFE_INTEGER)) {
    throw new globalThis.Error("Value is larger than Number.MAX_SAFE_INTEGER");
  }
  return long.toNumber();
}

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}
